import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, Zap, Shield, Users, BarChart3, Clock, 
  CheckCircle2, ArrowRight, Star, Send, Filter, Bot,
  ChevronDown, Play
} from 'lucide-react';

const PLANS = [
  {
    name: 'Starter',
    price: 29,
    description: 'Ideal para comenzar',
    features: [
      '1 perfil de envío',
      '500 mensajes/día',
      'Filtrado RCS básico',
      'Soporte por email',
      'Plantillas ilimitadas',
    ],
    highlighted: false,
  },
  {
    name: 'Pro',
    price: 79,
    description: 'Para negocios en crecimiento',
    features: [
      '3 perfiles de envío',
      '2,000 mensajes/día',
      'Campañas programadas',
      'Detección de respuestas',
      'A/B Testing',
      'Soporte prioritario',
    ],
    highlighted: true,
    badge: 'Más Popular',
  },
  {
    name: 'Business',
    price: 199,
    description: 'Para operaciones a escala',
    features: [
      '10 perfiles de envío',
      'Mensajes ilimitados',
      'Analytics avanzado',
      'API de integración',
      'Anti-detección avanzada',
      'Soporte dedicado 24/7',
    ],
    highlighted: false,
  },
];

const FEATURES = [
  {
    icon: Send,
    title: 'Envío Masivo RCS',
    description: 'Envía miles de mensajes RCS a través de Google Messages con automatización inteligente.',
  },
  {
    icon: Filter,
    title: 'Filtrado RCS',
    description: 'Identifica qué números tienen capacidad RCS antes de enviar para maximizar la efectividad.',
  },
  {
    icon: Shield,
    title: 'Anti-Detección',
    description: 'Sistema avanzado de fingerprinting y delays aleatorios para evitar bloqueos.',
  },
  {
    icon: Users,
    title: 'Multi-Perfil',
    description: 'Gestiona múltiples cuentas y distribuye el trabajo entre ellas automáticamente.',
  },
  {
    icon: BarChart3,
    title: 'Analytics en Tiempo Real',
    description: 'Dashboard con estadísticas detalladas, tasas de éxito y rendimiento por campaña.',
  },
  {
    icon: Bot,
    title: 'Detección de Respuestas',
    description: 'Monitorea automáticamente las respuestas de tus contactos en tiempo real.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Carlos M.',
    role: 'Director de Marketing',
    text: 'Incrementamos nuestro engagement un 300% usando mensajes RCS en lugar de SMS tradicionales.',
    avatar: 'CM',
  },
  {
    name: 'Ana R.',
    role: 'Emprendedora',
    text: 'La plataforma es increíblemente fácil de usar. Configuré mi primera campaña en minutos.',
    avatar: 'AR',
  },
  {
    name: 'Miguel S.',
    role: 'E-commerce Manager',
    text: 'El filtrado RCS nos ahorra tiempo y dinero al identificar qué clientes pueden recibir mensajes enriquecidos.',
    avatar: 'MS',
  },
];

const FAQ = [
  {
    question: '¿Qué es RCS y por qué es mejor que SMS?',
    answer: 'RCS (Rich Communication Services) es el sucesor del SMS tradicional. Permite enviar mensajes con imágenes de alta calidad, confirmaciones de lectura, indicadores de escritura y más, sin costo adicional para el usuario.',
  },
  {
    question: '¿Necesito una cuenta especial de Google?',
    answer: 'No, solo necesitas una cuenta de Google normal y la aplicación Google Messages instalada en un dispositivo Android. Nuestra extensión de Chrome automatiza el envío a través de messages.google.com.',
  },
  {
    question: '¿Qué incluye la prueba gratuita?',
    answer: 'La prueba gratuita de 7 días incluye todas las funciones del Plan Pro: 3 perfiles, 2,000 mensajes/día, campañas programadas y detección de respuestas.',
  },
  {
    question: '¿Puedo cancelar en cualquier momento?',
    answer: 'Sí, puedes cancelar tu suscripción cuando quieras. No hay contratos a largo plazo ni penalizaciones.',
  },
];

function FAQItem({ question, answer }) {
  return (
    <details className="group border-b border-zinc-800">
      <summary className="flex items-center justify-between cursor-pointer py-4 text-white font-medium hover:text-blue-400 transition-colors">
        {question}
        <ChevronDown className="w-5 h-5 text-zinc-500 group-open:rotate-180 transition-transform" />
      </summary>
      <p className="pb-4 text-zinc-400 text-sm leading-relaxed">{answer}</p>
    </details>
  );
}

export default function LandingPage() {
  const { user, login } = useAuth();

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090B]/80 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <span className="font-bold text-lg">RCS Sender</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors hidden sm:block">
                Características
              </a>
              <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors hidden sm:block">
                Precios
              </a>
              {user ? (
                <Link
                  to="/"
                  className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Ir al Panel
                </Link>
              ) : (
                <>
                  <button
                    onClick={login}
                    className="text-sm text-zinc-400 hover:text-white transition-colors"
                  >
                    Iniciar Sesión
                  </button>
                  <Link
                    to="/request-access"
                    className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Prueba Gratis
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-6">
            <Zap className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400 font-medium">7 días de prueba gratis</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            Automatiza tus mensajes{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              RCS
            </span>{' '}
            a escala
          </h1>
          
          <p className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
            Envía campañas masivas de mensajes RCS a través de Google Messages. 
            Mayor engagement que SMS, con imágenes HD y confirmaciones de lectura.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/request-access"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 px-8 py-3 rounded-lg font-semibold text-lg transition-all hover:scale-105 flex items-center justify-center gap-2"
            >
              Comenzar Prueba Gratis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#features"
              className="w-full sm:w-auto border border-zinc-700 hover:border-zinc-600 px-8 py-3 rounded-lg font-medium text-lg transition-colors flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Ver Demo
            </a>
          </div>
          
          <p className="mt-6 text-sm text-zinc-500">
            Sin tarjeta de crédito • Cancela cuando quieras
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-zinc-800/50 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-white">98%</p>
              <p className="text-sm text-zinc-500 mt-1">Tasa de entrega</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-white">5x</p>
              <p className="text-sm text-zinc-500 mt-1">Más engagement que SMS</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-white">10M+</p>
              <p className="text-sm text-zinc-500 mt-1">Mensajes enviados</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-white">500+</p>
              <p className="text-sm text-zinc-500 mt-1">Empresas activas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Todo lo que necesitas para campañas exitosas
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Herramientas profesionales para automatizar y escalar tus mensajes RCS
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature, i) => (
              <div
                key={`feature-${feature.title}`}
                className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-colors group"
              >
                <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-900/30 border-y border-zinc-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Cómo funciona
            </h2>
            <p className="text-zinc-400 text-lg">
              Empieza a enviar mensajes en menos de 5 minutos
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold mb-2">Instala la extensión</h3>
              <p className="text-zinc-400 text-sm">
                Descarga nuestra extensión de Chrome y conéctala con tu cuenta de Google Messages.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold mb-2">Sube tus contactos</h3>
              <p className="text-zinc-400 text-sm">
                Importa tu lista de contactos desde CSV o Excel. Filtra los que tienen RCS.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold mb-2">Lanza tu campaña</h3>
              <p className="text-zinc-400 text-sm">
                Crea tu mensaje, programa el envío y deja que la automatización trabaje por ti.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Planes simples y transparentes
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tus necesidades. Todos incluyen 7 días de prueba gratis.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map((plan, i) => (
              <div
                key={`plan-${plan.name}`}
                className={`relative rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'bg-gradient-to-b from-blue-600/20 to-blue-600/5 border-2 border-blue-500'
                    : 'bg-zinc-900/50 border border-zinc-800'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                  <p className="text-sm text-zinc-500">{plan.description}</p>
                </div>
                
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span className="text-zinc-500">/mes</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/request-access"
                  className={`block w-full text-center py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                  }`}
                >
                  Comenzar Prueba
                </Link>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-zinc-500 mt-8">
            * Todos los precios son en USD. Pagos manuales por el momento.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-900/30 border-y border-zinc-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Lo que dicen nuestros clientes
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={`testimonial-${t.name}`} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-zinc-300 text-sm mb-4 leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center text-sm font-semibold text-blue-400">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t.name}</p>
                    <p className="text-xs text-zinc-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Preguntas frecuentes
            </h2>
          </div>
          
          <div className="space-y-0">
            {FAQ.map((item, i) => (
              <FAQItem key={`faq-${item.question.slice(0,20)}`} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/20 rounded-3xl p-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Empieza tu prueba gratuita hoy
            </h2>
            <p className="text-zinc-400 text-lg mb-8 max-w-xl mx-auto">
              7 días con todas las funciones del Plan Pro. Sin compromiso, sin tarjeta de crédito.
            </p>
            <Link
              to="/request-access"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:scale-105"
            >
              Solicitar Acceso Gratuito
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Company Info Section — Required for Meta Business Verification */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-6">Empresa</h2>
          <div className="bg-zinc-900 border border-white/10 rounded-xl p-8 text-left space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Razon Social</p>
                <p className="text-lg font-bold text-white">RAUL HORACIO OSTENGO</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">CUIT</p>
                <p className="text-lg font-bold text-white">20-26638702-5</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Domicilio Legal</p>
                <p className="text-sm text-zinc-300">San Martin 839, Piso 3, Depto A</p>
                <p className="text-sm text-zinc-300">Entre calles Salta y Junin</p>
                <p className="text-sm text-zinc-300">San Miguel de Tucuman (4000), Tucuman, Argentina</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Contacto</p>
                <p className="text-sm text-zinc-300">Tel: +54 9 381 414 9687</p>
                <p className="text-sm text-zinc-300">Email: soporte@rcsender.com</p>
                <p className="text-sm text-zinc-300">Web: https://rcsender.com</p>
              </div>
            </div>
            <div className="pt-4 border-t border-white/5 flex flex-wrap gap-4 text-sm">
              <a href="/terms" className="text-blue-400 hover:text-blue-300 transition-colors">Terminos y Condiciones</a>
              <a href="/privacy" className="text-blue-400 hover:text-blue-300 transition-colors">Politica de Privacidad</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <span className="font-bold">RCS Sender</span>
            </div>
            <p className="text-sm text-zinc-500">
              &copy; {new Date().getFullYear()} RCS Sender — RAUL HORACIO OSTENGO. CUIT: 20-26638702-5
            </p>
            <p className="text-xs text-zinc-600 mt-1">
              San Martin 839, Piso 3, Depto A - Entre calles Salta y Junin — San Miguel de Tucuman (4000), Tucuman, Argentina — Tel: +54 9 381 414 9687
            </p>
            <div className="flex items-center gap-6 mt-3">
              <a href="/terms" className="text-sm text-zinc-500 hover:text-white transition-colors">
                Terminos y Condiciones
              </a>
              <a href="/privacy" className="text-sm text-zinc-500 hover:text-white transition-colors">
                Politica de Privacidad
              </a>
              <a href="mailto:soporte@rcsender.com" className="text-sm text-zinc-500 hover:text-white transition-colors">
                soporte@rcsender.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
